<!-- src/components/InfoTab.vue -->
<template>
  <div class="info-tab-container">
    <!-- Flag and Location Header -->
    <div class="location-header">
      <div class="flag-container">
        <img :src="flagUrl" alt="Country flag" class="flag-image">
      </div>
      <div class="location-info">
        <h2 class="country-name">{{ infoData.name || 'Unknown City' }}</h2>
        <p class="location-details" v-if="infoData.territorial_divisions">
          <i class="fas fa-map-marker-alt location-icon"></i>
          {{ infoData.territorial_divisions.region || '' }}{{ infoData.territorial_divisions.region && infoData.territorial_divisions.city ? ', ' : '' }}{{ infoData.territorial_divisions.city || '' }},
          {{ infoData.country }}
        </p>
      </div>
      <div class="header-actions">
        <StarButton
          v-if="infoData.slug"
          :item-id="infoData.slug"
          item-type="place"
          :item-data="{ name: infoData.name, country: infoData.country }"
          size="medium"
          variant="default"
        />
        <button @click="sharePlace" class="share-btn" title="Share this place">
          <i class="fas fa-share-alt"></i>
        </button>
      </div>
    </div>
    
    <!-- Share Card for Desktop -->
    <div v-if="showShareCard && !isMobile" class="share-card">
      <div class="share-card-header">
        <i class="fas fa-link"></i>
        <span>Share this place</span>
        <button @click="closeShareCard" class="share-close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="share-url-container">
        <input ref="shareUrlInput" :value="shareUrl" readonly class="share-url-input">
        <button @click="copyUrl" class="copy-btn" :class="{ 'copied': urlCopied }">
          <i :class="urlCopied ? 'fas fa-check' : 'fas fa-copy'"></i>
          {{ urlCopied ? 'Copied!' : 'Copy' }}
        </button>
      </div>
    </div>

    <!-- Action Button at Top -->
    <div class="action-section">
      <button @click="openModal" class="details-button">
        <i class="fas fa-external-link-alt"></i>
        Show All Details
      </button>
    </div>

    <!-- Description Card -->
    <div class="description-card">
      <h3 class="section-title">
        <i class="fas fa-info-circle"></i>
        About this place
      </h3>
      <p class="description-text">{{ infoData.description || 'No description available' }}</p>
    </div>


    <!-- History Card -->
    <div class="history-card">
      <h3 class="section-title">
        <i class="fas fa-scroll"></i>
        History
      </h3>
      <p class="history-text">{{ infoData.history || 'No historical information available' }}</p>
    </div>

    <!-- Landmarks Card -->
    <div v-if="infoData.landmarks && infoData.landmarks.length" class="landmarks-card">
      <h3 class="section-title">
        <i class="fas fa-monument"></i>
        Landmarks
      </h3>
      <ul class="places-list">
        <li v-for="landmark in infoData.landmarks" :key="landmark.name" class="place-item">
          <div class="place-content">
            <img v-if="landmark.thumbnail" :src="landmark.thumbnail" :alt="landmark.name" class="place-thumbnail" @click="viewImages(landmark.name, landmark.slug)" title="Click to view images">
            <div class="place-info">
              <div class="place-name">{{ landmark.name }}</div>
              <div class="place-description">{{ landmark.description }}</div>
            </div>
            <div class="place-actions">
              <button
                @click="viewImages(landmark.name, landmark.slug)"
                class="action-button image-button"
                :title="`View images of ${landmark.name}`"
              >
                <i class="fas fa-images"></i>
              </button>
              <button
                @click="getMoreInfo(landmark.name, landmark.slug)"
                class="action-button info-button"
                :title="`More information about ${landmark.name}`"
              >
                <i class="fas fa-info-circle"></i>
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <!-- Famous Places Card -->
    <div v-if="infoData.famous_places && infoData.famous_places.length" class="famous-places-card">
      <h3 class="section-title">
        <i class="fas fa-star"></i>
        Famous Places
      </h3>
      <ul class="places-list">
        <li v-for="place in infoData.famous_places" :key="place.name" class="place-item">
          <div class="place-content">
            <img v-if="place.thumbnail" :src="place.thumbnail" :alt="place.name" class="place-thumbnail" @click="viewImages(place.name, place.slug)" title="Click to view images">
            <div class="place-info">
              <div class="place-name">{{ place.name }}</div>
              <div class="place-description">{{ place.description }}</div>
            </div>
            <div class="place-actions">
              <button
                @click="viewImages(place.name, place.slug)"
                class="action-button image-button"
                :title="`View images of ${place.name}`"
              >
                <i class="fas fa-images"></i>
              </button>
              <button
                @click="getMoreInfo(place.name, place.slug)"
                class="action-button info-button"
                :title="`More information about ${place.name}`"
              >
                <i class="fas fa-info-circle"></i>
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <!-- Stats Card -->
    <div class="stats-card">
      <h3 class="section-title">
        <i class="fas fa-chart-bar"></i>
        Quick Stats
      </h3>
      <div class="stats-grid">
        <div v-if="infoData.country_population" class="stat-item">
          <i class="fas fa-users stat-icon"></i>
          <div class="stat-content">
            <span class="stat-label">Country Pop.</span>
            <span class="stat-value">{{ infoData.country_population }}</span>
          </div>
        </div>
        <div v-if="infoData.country_extension" class="stat-item">
          <i class="fas fa-map stat-icon"></i>
          <div class="stat-content">
            <span class="stat-label">Country Area</span>
            <span class="stat-value">{{ infoData.country_extension }}</span>
          </div>
        </div>
        <div v-if="infoData.division_population" class="stat-item">
          <i class="fas fa-user-friends stat-icon"></i>
          <div class="stat-content">
            <span class="stat-label">Local Pop.</span>
            <span class="stat-value">{{ infoData.division_population }}</span>
          </div>
        </div>
        <div v-if="infoData.division_extension" class="stat-item">
          <i class="fas fa-expand-arrows-alt stat-icon"></i>
          <div class="stat-content">
            <span class="stat-label">Local Area</span>
            <span class="stat-value">{{ infoData.division_extension }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Languages Card -->
    <div v-if="infoData.languages_spoken && infoData.languages_spoken.length" class="languages-card">
      <h3 class="section-title">
        <i class="fas fa-language"></i>
        Languages
      </h3>
      <div class="languages-list">
        <div v-for="language in infoData.languages_spoken.slice(0, 3)" :key="language.iso_639_1" class="language-item">
          <span class="language-name">{{ language.name }}</span>
          <span v-if="language.approx_percentage" class="language-percentage">{{ language.approx_percentage }}</span>
          <span class="language-status" :class="language.official_status">{{ language.official_status }}</span>
        </div>
        <div v-if="infoData.languages_spoken.length > 3" class="more-languages">
          +{{ infoData.languages_spoken.length - 3 }} more
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StarButton from './StarButton.vue';

export default {
  name: 'InfoTab',
  components: {
    StarButton
  },
  emits: ['open-modal', 'close'],
  props: {
    infoData: {
      type: Object,
      required: true
    },
    flagUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showShareCard: false,
      urlCopied: false,
      isMobile: window.innerWidth <= 768
    };
  },
  computed: {
    shareUrl() {
      if (this.infoData && this.infoData.slug) {
        return `${window.location.origin}/location/${this.infoData.slug}`;
      }
      return '';
    }
  },
  mounted() {
    window.addEventListener('resize', this.checkScreenSize);
    console.log('[InfoTab] Mounted with infoData:', this.infoData);
    console.log('[InfoTab] infoData.slug:', this.infoData?.slug);
  },
  watch: {
    infoData(newVal) {
      console.log('[InfoTab] infoData changed:', newVal);
      console.log('[InfoTab] infoData.slug:', newVal?.slug);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  methods: {
    openModal() {
      console.log('openModal');

      document.addEventListener('keydown', this.handleKeydown); // Add event listener

      this.$emit('open-modal');
    },
    closeModal() {
      console.log('closeModal method');
      this.$emit('close');
      document.removeEventListener('keydown', this.handleKeydown); // Remove event listener
    },
    viewImages(placeName, itemSlug) {
      console.log(`Opening images modal for: ${placeName} with slug: ${itemSlug}`);
      this.$store.dispatch('images/openModal', {
        placeName: placeName,
        country: this.infoData.country,
        itemSlug: itemSlug
      });
    },
    getMoreInfo(placeName, itemSlug) {
      console.log(`Opening more info modal for: ${placeName} (item: ${itemSlug})`);
      this.$store.dispatch('moreInfo/openModal', {
        placeName: placeName,
        country: this.infoData.country,
        placeSlug: this.infoData.slug,
        itemSlug: itemSlug
      });
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
    },
    sharePlace() {
      if (this.isMobile) {
        this.shareOnMobile();
      } else {
        this.showShareCard = true;
      }
    },
    async shareOnMobile() {
      const shareData = {
        title: `${this.infoData.name} - Captain Leo Maps`,
        text: `Check out this amazing place: ${this.infoData.name}`,
        url: this.shareUrl
      };

      try {
        if (navigator.share) {
          await navigator.share(shareData);
        } else {
          // Fallback for browsers that don't support Web Share API
          this.copyUrl();
        }
      } catch (error) {
        console.log('Error sharing:', error);
        // Fallback to copy URL
        this.copyUrl();
      }
    },
    closeShareCard() {
      this.showShareCard = false;
      this.urlCopied = false;
    },
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        this.urlCopied = true;
        setTimeout(() => {
          this.urlCopied = false;
          if (!this.isMobile) {
            this.showShareCard = false;
          }
        }, 2000);
      } catch (error) {
        console.log('Failed to copy URL:', error);
        // Fallback for older browsers
        if (this.$refs.shareUrlInput) {
          this.$refs.shareUrlInput.select();
          document.execCommand('copy');
          this.urlCopied = true;
          setTimeout(() => {
            this.urlCopied = false;
            if (!this.isMobile) {
              this.showShareCard = false;
            }
          }, 2000);
        }
      }
    },
  }
};
</script>

<style scoped>
@import '~@fortawesome/fontawesome-free/css/all.css';

.info-tab-container {
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Location Header */
.location-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  position: relative;
}

.location-header:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.flag-container {
  flex-shrink: 0;
}

.flag-image {
  width: 80px;
  height: auto;
  max-height: 60px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.8);
}

.location-info {
  flex: 1;
  min-width: 0;
}

.country-name {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.location-details {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.location-icon {
  color: #667eea;
  font-size: 12px;
}

/* Content Cards */
.description-card,
.history-card,
.landmarks-card,
.famous-places-card {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
}

.description-card:hover,
.history-card:hover,
.landmarks-card:hover,
.famous-places-card:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title i {
  color: #667eea;
  font-size: 14px;
}

.description-text,
.history-text {
  font-size: 14px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
}

/* Famous Places List */
.places-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.place-item {
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  transition: all 0.2s ease;
}

.place-item:hover {
  background: rgba(255, 255, 255, 0.9);
  border-color: #cbd5e1;
  transform: translateX(4px);
}

.place-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.place-thumbnail {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
}

.place-thumbnail:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border-color: #667eea;
}

.place-info {
  flex: 1;
  min-width: 0;
}

.place-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
  margin-bottom: 4px;
}

.place-description {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

.place-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.action-button {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  position: relative;
}

.action-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.image-button {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.image-button:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.info-button {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.info-button:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

/* Tooltip styling for button titles */
.action-button:hover::after {
  content: attr(title);
  position: absolute;
  bottom: -28px;
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
}

.action-button:hover::before {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-bottom: 4px solid #1f2937;
  z-index: 1001;
  pointer-events: none;
}

/* Action Section */
.action-section {
  padding-top: 8px;
}

.details-button {
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  font-weight: 600;
  padding: 14px 20px;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 15px;
  cursor: pointer;
}

.details-button:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.details-button:active {
  transform: translateY(0);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
}

.details-button i {
  font-size: 14px;
}

/* Stats Card */
.stats-card,
.languages-card {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
}

.stats-card:hover,
.languages-card:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.stat-icon {
  color: #667eea;
  font-size: 14px;
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.stat-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.stat-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  line-height: 1;
}

.stat-value {
  font-size: 12px;
  color: #1e293b;
  font-weight: 600;
  line-height: 1.2;
  margin-top: 2px;
}

/* Languages */
.languages-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.language-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  font-size: 12px;
}

.language-name {
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  min-width: 0;
}

.language-percentage {
  color: #667eea;
  font-weight: 500;
  font-size: 11px;
}

.language-status {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.language-status.official {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.language-status.co-official {
  background: #dbeafe;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

.language-status.regional {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fed7aa;
}

.language-status.minority {
  background: #f3f4f6;
  color: #4b5563;
  border: 1px solid #d1d5db;
}

.more-languages {
  text-align: center;
  padding: 4px;
  color: #64748b;
  font-size: 11px;
  font-style: italic;
}

/* Responsive Design */
@media (max-width: 768px) {
  .info-tab-container {
    gap: 16px;
  }
  
  .location-header {
    padding: 16px;
    gap: 12px;
  }
  
  .flag-image {
    width: 60px;
    max-height: 45px;
  }
  
  .country-name {
    font-size: 20px;
  }
  
  .location-details {
    font-size: 13px;
  }
  
  .description-card,
  .history-card,
  .landmarks-card,
  .famous-places-card {
    padding: 14px;
  }
  
  .details-button {
    padding: 12px 16px;
    font-size: 14px;
  }
  
  .place-content {
    gap: 8px;
  }
  
  .place-thumbnail {
    width: 56px;
    height: 56px;
  }
  
  .place-actions {
    justify-content: flex-end;
    margin-top: 8px;
  }
  
  .action-button {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
  
  .stats-card,
  .languages-card {
    padding: 14px;
  }
  
  .stats-grid {
    gap: 10px;
  }
  
  .stat-item {
    padding: 6px;
    gap: 6px;
  }
  
  .stat-label {
    font-size: 10px;
  }
  
  .stat-value {
    font-size: 11px;
  }
  
  .language-item {
    padding: 5px 6px;
    gap: 4px;
    font-size: 11px;
  }
  
  .language-status {
    font-size: 9px;
    padding: 1px 4px;
  }
}

/* Header Actions Container */
.header-actions {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 8px;
  align-items: center;
}

/* Share Button */
.share-btn {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
  font-size: 16px;
}

.share-btn:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.share-btn:active {
  transform: translateY(0);
}

/* Share Card */
.share-card {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.share-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-weight: 600;
  color: #374151;
}

.share-card-header i {
  color: #667eea;
}

.share-close-btn {
  margin-left: auto;
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.share-close-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #374151;
}

.share-url-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.share-url-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: white;
  font-size: 13px;
  color: #374151;
  min-width: 0;
}

.share-url-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.copy-btn {
  padding: 10px 16px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  min-width: 80px;
  justify-content: center;
}

.copy-btn:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-1px);
}

.copy-btn.copied {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.copy-btn.copied:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

/* Responsive adjustments for header actions */
@media (max-width: 768px) {
  .header-actions {
    top: 16px;
    right: 16px;
    gap: 6px;
  }

  .share-btn {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
  
  .share-card {
    padding: 14px;
    margin: 0 -4px 8px -4px;
  }
  
  .share-url-container {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .copy-btn {
    width: 100%;
    justify-content: center;
    padding: 12px;
  }
}
</style>
